<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { isAxiosError } from 'axios';
import router from '@/router';
import AuthenticationService from '@/services/AuthenticationService';
import { useUserStore } from '@/stores/user';
import FsumHeader from '@/components/FsumHeader.vue';

const $q = useQuasar();
const userStore = useUserStore();
const accessRestricted = ref<boolean>(false);

const login = async () => {
    $q.loading.show({
        message: 'Loading... Please wait',
    });

    try {
        const response = await AuthenticationService.login();
        userStore.authenticate(response);
        router.push({ name: 'default' });
    } catch (error: unknown) {
        if (isAxiosError(error)) {
            if (error.response?.status === 403) {
                accessRestricted.value = true;
            }
        }
    } finally {
        $q.loading.hide();
    }
};

if (userStore.isAuthenticated()) {
    router.push({ name: 'default' });
} else {
    login();
}

const supportUrl = import.meta.env.VITE_SUPPORT_CONTACT_URL;
const personalClassroomUrl = import.meta.env.VITE_PERSONAL_CLASSROOM_URL;
</script>
<template>
    <q-layout view="hHh LpR fFf">
        <FsumHeader :toggleable-side-nav="false" />
        <q-page-container
            id="fsum-main-panel"
            class="col-10 bg-gray"
        >
            <div
                v-if="accessRestricted"
                class="container"
            >
                <div class="row q-mx-auto q-my-lg q-py-lg">
                    <div class="col-lg-7 col-md-8 col-sm-10 col-12 q-mx-auto bg-white restricted-access">
                        <h1 class="header">
                            You do not have access to this resource.
                        </h1>
                        <span class="subtitle">
                            Please
                            <a :href="supportUrl">contact Gleim Support</a>
                            if you believe this is an error, or return to your
                            <a :href="personalClassroomUrl">Personal Classroom</a>.
                        </span>
                    </div>
                </div>
            </div>
        </q-page-container>
    </q-layout>
</template>
<style scoped lang="scss">
#fsum-main-panel {
    min-height: 100vh;
    z-index: 1;
    padding: 24px;

    @media screen and (max-width: $breakpoint-md-min) {
        padding: 16px;
    }

    .restricted-access {
        padding: 2.5rem;

        .header {
            font-size: 2rem;
            font-weight: 400;
            line-height: 1rem;
            letter-spacing: normal;
            margin: 0.5rem 0 2rem;
        }

        .subtitle {
            font-size: 1.25rem;
            font-weight: 400;
            line-height: 1rem;

            a {
                color: var(--fsum-blue);
                font-weight: 400;
            }
        }

        @media screen and (max-width: $breakpoint-md-min) {
            padding: 1.5rem;

            .header {
                font-size: 1.5rem;
            }

            .subtitle {
                font-size: 1rem;
            }
        }

        @media screen and (max-width: $breakpoint-sm-min) {
            padding: 0.5rem;

            .header {
                font-size: 1.25rem;
            }

            .subtitle {
                font-size: 0.875rem;
            }
        }
    }
}
</style>
